import { SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { hasControlCharacter, safeHref } from '@shared';
import { withTaskListCheckboxes } from '../shared/markdown';

/**
 * Attributes Angular's sanitizer keeps that make the browser fetch a URL without a click. Keep in
 * step with Angular's allowlist; only a Content-Security-Policy would make this a guarantee.
 */
const AUTO_LOADING_ATTRIBUTES = ['src', 'srcset', 'poster', 'background'];

/**
 * Only http(s) links leave the page. `mailto:` hands off to the mail client without navigating, and a
 * blank tab opened for it is left behind empty; a fragment or relative link is not "a web link" in the
 * sense SB-4011 asks for. By the time it runs, the only hrefs left are `https:`, `mailto:` and
 * relative ones (`chatLinkHref`, below); `https?` stays so this step stays correct whatever runs
 * before it. The response-table widget renders its own anchors and already opens them in a new tab
 * (`response-table.inline.ts`).
 */
const WEB_LINK_RE = /^https?:/i;

/** No link names these by accident: `.invalid` never resolves (RFC 2606). Two, so a link that names one is caught by the other. */
const RELATIVE_BASES = ['https://a.chat.invalid/', 'https://b.chat.invalid/'];
/** RFC 3986 path characters, with no `%`: nothing a desktop app could decode, trim or fold into a share path. */
const PLAIN_PATH_RE = /^[A-Za-z0-9\-._~!$&'()*+,;=:@/]*$/;

/** Whether a relative href's path is plain, and stays a single-slash path once dot segments fold. */
const isPlainPath = (href: string): boolean => {
  const path = href.split(/[?#]/)[0] ?? '';
  return (
    PLAIN_PATH_RE.test(path) &&
    !path.startsWith('//') &&
    RELATIVE_BASES.every(
      (base) => !new URL(href, base).pathname.startsWith('//'),
    )
  );
};

/**
 * The href to keep live in the chat, or `null` to render the link as its words. The rule and its
 * reasons are in `connectorSafeMarkdown`'s docblock.
 */
const chatLinkHref = (href: string): string | null => {
  if (hasControlCharacter(href)) return null;
  try {
    const relative = RELATIVE_BASES.every(
      (base) => new URL(href, base).origin === new URL(base).origin,
    );
    // No host of its own (`#x`, `?q`, a path): kept as written when its path is plain.
    if (relative) return isPlainPath(href) ? href : null;
  } catch {
    return null;
  }
  return safeHref(href);
};

/**
 * Sanitize answer HTML, then remove everything that loads a URL without a click, so an answer cannot
 * make the browser call a host of its choosing. Attachments render separately.
 *
 * As the app's one `SANITIZE` step it also turns task-list checkboxes into markup the sanitizer keeps
 * (`withTaskListCheckboxes`, SB-3281), which has to happen before sanitizing strips them.
 *
 * It also opens web links in a new tab (SB-4011): an answer's anchor is a plain `<a href>`, not a
 * `routerLink`, so following one in place unloads the app and the conversation with it. This is done
 * here, on the sanitized HTML, and not in a marked renderer extension, because ngx-markdown registers
 * extensions on marked's global instance, which the chart recognizer needs stock
 * (`shared/markdown/marked-instance.guard.spec.ts`). It also reaches anchors an answer writes as raw HTML, which
 * marked's `link` renderer never sees, and an image-map `area`, which navigates the same way. Its
 * `target` and `rel` are constants added after sanitizing. Any `target`, `rel`, `download` or `accesskey`
 * the answer wrote itself is dropped first, on every link: the sanitizer keeps all four. An author's
 * `rel="opener"` on a relative link this step does not open would hand the page it opens a handle
 * on the app's tab, `download` would let the answer name the file a same-origin link saves as, and
 * `accesskey` would let a keystroke follow a live link.
 *
 * Before that, every `a[href]` and `area[href]` goes through `chatLinkHref`.
 * A link that names its own scheme or host must pass `safeHref` (`@shared`), the rule the response
 * table and the Word export use: `https:` and `mailto:` stay, `http://` becomes `https://`, and
 * every other scheme (Angular's `unsafe:` and `blob:` included) and every network-path spelling
 * (`//host`, `/\host`, with hidden whitespace or controls) becomes its words. A link that names
 * neither (`#x`, `?q`, a relative path) stays as written, provided its path holds only plain
 * RFC 3986 characters, with no `%` and no leading `//` before or after its dot segments fold: in
 * the page it is a same-origin path, but Copy's `text/html` hands it as written to a desktop app,
 * which could decode, trim or fold a less plain one into a share path. An allowlist, as `safeHref`
 * is, because each list of refused spellings met a new one; its cost is that a relative path with
 * a percent-escape, a space or an accented letter becomes its words. A control character refuses
 * any link, as in `safeHref`. Kept `https:` and `mailto:` links still carry whatever their address
 * holds (Article XXII gap (k)).
 *
 * Which links name a scheme or host is decided by the WHATWG URL parser, the one the browser
 * navigates with, not a prefix test: Angular leaves `//host` and its hidden-whitespace spellings
 * without an `unsafe:` prefix, and the parser strips and folds them as the browser would. A link
 * counts as relative only when it lands on the origin of each of two fixed `.invalid` bases, so a
 * link that names one base is caught by the other. The bases are constants, not `document.baseURI`
 * or `location`, so the rule does not depend on where the page is served (the link fixture's recorder runs
 * under `about:blank`).
 *
 * One of the two layers core-ai#538's prefix fixture relies on, with `makeLinksInert`.
 * Any change to either layer, or to a reader or a step before one, re-runs SB-3978 T34's fixture and fuzz (core-ai#538), with no prefix skip.
 */
export const connectorSafeMarkdown =
  (sanitizer: DomSanitizer) =>
  (html: string): string => {
    const safe =
      sanitizer.sanitize(SecurityContext.HTML, withTaskListCheckboxes(html)) ??
      '';
    const template = document.createElement('template');
    template.innerHTML = safe;
    template.content
      .querySelectorAll('img, picture, source, video, audio, track')
      .forEach((element) => {
        element.replaceWith(
          document.createTextNode(element.getAttribute('alt') ?? ''),
        );
      });
    template.content
      .querySelectorAll(
        AUTO_LOADING_ATTRIBUTES.map((name) => `[${name}]`).join(', '),
      )
      .forEach((element) => {
        AUTO_LOADING_ATTRIBUTES.forEach((name) =>
          element.removeAttribute(name),
        );
      });
    template.content
      .querySelectorAll('a[href], area[href]')
      .forEach((anchor) => {
        const href = anchor.getAttribute('href') ?? '';
        const live = chatLinkHref(href);
        if (live === null) {
          const text = document.createElement('span');
          text.textContent = anchor.textContent;
          anchor.replaceWith(text);
        } else if (live !== href) {
          anchor.setAttribute('href', live);
        }
      });
    template.content
      .querySelectorAll('a[href], area[href]')
      .forEach((anchor) => {
        anchor.removeAttribute('target');
        anchor.removeAttribute('rel');
        anchor.removeAttribute('download');
        anchor.removeAttribute('accesskey');
        if (WEB_LINK_RE.test(anchor.getAttribute('href') ?? '')) {
          anchor.setAttribute('target', '_blank');
          anchor.setAttribute('rel', 'noopener noreferrer');
        }
      });
    return template.innerHTML;
  };
